import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import NonRKPayInvoiceEstimatesService from "@services/NonRKPayInvoiceEstimatesService";
import { NONRKPAY_INVOICE_ESTIMATES_REFUNDABLE_ERROR_CODES } from "@constants/index";
import Modal from "../../../../../components/Modal/Modal";
import { estimateAssociatedInvoicesArePaid } from "./NonRKPayInvoiceToolbar/components/ToolbarActionHelpers";

const RefundNonRKPayInvoiceEstimateModal = ({ show, estimateId, onConfirm, onCancel, nonRKPayInvoiceDetailHook }) => {
	const { lang } = global;
	const [refundingEstimate, setRefundingEstimate] = useState(false);
	const [loadingRefundAmount, setLoadingRefundAmount] = useState(false);
	const [valid, setIsValid] = useState(false);
	const [refundAmount, setRefundAmount] = useState(0);
	const [validationErrors, setValidationErrors] = useState([]);
	const [modalText, setModalText] = useState("");

	const {
		associatedInvoices,
	} = nonRKPayInvoiceDetailHook;

	const isEstimateInvoiced = associatedInvoices?.length > 0 && estimateAssociatedInvoicesArePaid(associatedInvoices);

const onHide = () => {
		onCancel();
	};

	const getEstimateRefundableErrorMessage = (errors) => {
		const errorMessagesMap = {
			[NONRKPAY_INVOICE_ESTIMATES_REFUNDABLE_ERROR_CODES.HAS_REFUNDS_IN_TRANSIT]: "Estimate has refunds in transit_",
			[NONRKPAY_INVOICE_ESTIMATES_REFUNDABLE_ERROR_CODES.ESTIMATE_HAS_INVOICES_NOT_SENT_TO_CLIENT_AND_PAID]: "Estimate has invoices not sent to client and paid_",
			[NONRKPAY_INVOICE_ESTIMATES_REFUNDABLE_ERROR_CODES.HAS_STRIPE_PENDING_PAYMENTS]: "Estimate has pending payments_",
			[NONRKPAY_INVOICE_ESTIMATES_REFUNDABLE_ERROR_CODES.HAS_UPCOMING_JOB_PRESENT]: "Estimate has upcoming job present_",
			[NONRKPAY_INVOICE_ESTIMATES_REFUNDABLE_ERROR_CODES.ESTIMATE_NOT_FOUND]: "Estimate not found_",
		};

		if (!errors?.length) return lang.t("Estimate is not refundable_");

		const messages = errors.map(error => {
			const messageKey = errorMessagesMap[error.ErrorCode] || "Estimate is not refundable_";
			return lang.t(messageKey);
		});

		return messages.join(", ");
	};

	const determineModalText = () => {
		let text = lang.t((isEstimateInvoiced ? "dynamic.EstimateRefundAndArchiveModalWithAssociatedInvoicesText" : "dynamic.EstimateRefundAndArchiveModalWithNoJobsWorkedText"), { refundAmount });

		if (!valid && validationErrors.length > 0) {
			text = getEstimateRefundableErrorMessage(validationErrors);
		}
		setModalText(text);
	}

	const getEstimateRefundableAmount = async () => {
		try {
			setLoadingRefundAmount(true);
			const { data } = await NonRKPayInvoiceEstimatesService.getEstimateRefundable(estimateId);
			setIsValid(data.Valid);
			setRefundAmount(data.RefundAmount);
			setValidationErrors(data.Errors || []);
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Error getting estimate refundable amount"),
				title: "Error",
			});
		} finally {
			setLoadingRefundAmount(false);
		}
	};

	const handleOnRefund = async () => {
		try {
			setRefundingEstimate(true);
			await onConfirm();
			onHide();
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Error refunding estimate"),
				title: "Error",
			});
		} finally {
			setRefundingEstimate(false);
		}
	};

	useEffect(() => {
		if (show && estimateId) {
			getEstimateRefundableAmount();
		}
	}, [show, estimateId]);

	useEffect(() => {
		determineModalText();
	}, [valid, refundAmount, validationErrors]);

	return (
		<Modal
			id="refund-nonrkpayinvoiceestimate-modal"
			show={show}
			onHide={onHide}
		>
			<Modal.Header>
				<Modal.Title>{lang.t("EstimateRefundAndArchiveModalTitle")}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="row text-center">
				<div className="col-xs-12">
					{!isEstimateInvoiced &&
						(<p>{lang.t("EstimateRefundAndArchiveModalWithNoJobsWorkedMessage")}</p>)
					}
					<i
						className={`rkf rkf-spinner rkf-spin rkf-fw${loadingRefundAmount ? "" : " hidden"}`}
					/>
					<p className={loadingRefundAmount ? "hidden" : ""}>
						{modalText}
					</p>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					id="refund-nonrkpayinvoiceestimate-modal-cancel-button"
					type="button"
					className="btn btn-link"
					onClick={() => onCancel()}
				>
					{lang.t("Cancel")}
				</button>
				<button
					id="refund-nonrkpayinvoiceestimate-modal-confirm-button"
					type="button"
					className="btn btn-primary"
					onClick={() => handleOnRefund()}
					disabled={refundingEstimate || loadingRefundAmount || (valid === false)}
				>
					<If condition={refundingEstimate || loadingRefundAmount}>
						<i className="rkf rkf-spinner rkf-spin rkf-fw" />
					</If>
					<If condition={!refundingEstimate && !loadingRefundAmount}>
						{lang.t("EstimateRefundAndArchiveModalMainButton")}
					</If>
				</button>
			</Modal.Footer>
		</Modal>
	);
};

RefundNonRKPayInvoiceEstimateModal.propTypes = {
	show: PropTypes.bool,
	estimateId: PropTypes.string.isRequired,
	onCancel: PropTypes.func,
	onConfirm: PropTypes.func,
	nonRKPayInvoiceDetailHook: PropTypes.object.isRequired,
};

RefundNonRKPayInvoiceEstimateModal.defaultProps = {
	show: false,
	onCancel: () => {},
	onConfirm: () => {},
};

export default RefundNonRKPayInvoiceEstimateModal;
